import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Avatar, 
  Stack, 
  CircularProgress, 
  Alert, 
  IconButton, 
  Tooltip,
  Collapse,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  useTheme
} from '@mui/material';
import { useAppSelector, useAppDispatch } from '../store';
import MessageBubble from './MessageBubble';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoIcon from '@mui/icons-material/Info';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import { getConversationEndpoint } from '../config/api';
import ShareButton from './ShareButton';
import ShareModal from './ShareModal';

// Utility function to get JWT from cookie
const getJwtFromCookie = () => {
  const match = document.cookie.match(/(?:^|; )jwt=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
};

interface Message {
  _id: string;
  conversationId: string;
  userId: string;
  content: string;
  type: 'user' | 'ai';
  status: string;
  aiResponse?: {
    content: string;
    model?: string;
    tokensUsed?: number;
    processingTime?: number;
    confidence?: number;
    suggestions?: string[];
    actions?: Array<{
      type: 'automation' | 'suggestion' | 'analysis';
      description: string;
      data?: any;
    }>;
    image?: string;
  };
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

interface ConversationWithMessages {
  _id: string;
  userId: string;
  title: string;
  status: 'active' | 'archived' | 'deleted';
  type: 'screenshare' | 'general';
  isPinned?: boolean;
  metadata?: {
    screenCaptureMode?: 'full' | 'window' | 'region';
    aiModel?: string;
    sessionDuration?: number;
    messageCount?: number;
    lastActivity?: Date;
    lastMessageAt?: Date;
  };
  messages?: Message[];
  messageCount?: number;
  createdAt: string;
  updatedAt: string;
}

const getConversationImages = (messages: Message[] = []) =>
  messages
    .map(msg => msg.aiResponse?.image)
    .filter((img): img is string => !!img);

const ConversationPanel: React.FC = () => {
  const activeConversationId = useAppSelector((state) => state.conversations.activeConversationId);
  const activeConversation = useAppSelector((state) => activeConversationId ? state.conversations.items[activeConversationId] : null);
  const dispatch = useAppDispatch();
  const [conversation, setConversation] = useState<ConversationWithMessages | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isClosed, setIsClosed] = useState(false);
  const [metadataExpanded, setMetadataExpanded] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareText, setShareText] = useState('');
  const [shareMessages, setShareMessages] = useState<Message[]>([]);
  const [shareImages, setShareImages] = useState<string[]>([]);
  
  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<string>('all');
  
  const theme = useTheme();

  // Filter messages based on search and filter criteria
  const filteredMessages = React.useMemo(() => {
    if (!conversation?.messages) return [];
    
    let filtered = conversation.messages;
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(message => 
        message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (message.aiResponse?.content && message.aiResponse.content.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(message => message.type === filterType);
    }
    
    // Date filter
    if (filterDate !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getFullYear(), now.getMonth() - 1, now.getDate());
      
      filtered = filtered.filter(message => {
        const messageDate = new Date(message.createdAt);
        switch (filterDate) {
          case 'today':
            return messageDate >= today;
          case 'week':
            return messageDate >= weekAgo;
          case 'month':
            return messageDate >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    return filtered;
  }, [conversation?.messages, searchTerm, filterType, filterDate]);

  console.log('ConversationPanel: activeConversationId:', activeConversationId);
  console.log('ConversationPanel: activeConversation:', activeConversation);
  


  // Fetch conversation and messages when activeConversationId changes
  useEffect(() => {
    // Reset closed state whenever activeConversationId changes
    setIsClosed(false);
    
    if (!activeConversationId) {
      setConversation(null);
      setError(null);
      return;
    }
    
    const fetchConversation = async () => {
      setLoading(true);
      setError(null);
      try {
        const jwt = getJwtFromCookie();
        if (!jwt) throw new Error('No authentication token found');
        
        const endpoint = getConversationEndpoint(activeConversationId);
        
        const response = await fetch(endpoint, {
          headers: {
            'Authorization': `Bearer ${jwt}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
        
        if (!response.ok) throw new Error(`Failed to fetch conversation: ${response.statusText}`);
        
        const responseData = await response.json();
        
        if (responseData.success && responseData.data) {
          console.log('🔍 ConversationPanel: Received conversation data:', responseData.data);
          console.log('🔍 ConversationPanel: Messages:', responseData.data.messages);
          if (responseData.data.messages) {
            responseData.data.messages.forEach((msg: any, idx: number) => {
              console.log(`🔍 Message ${idx}:`, {
                id: msg._id,
                type: msg.type,
                content: msg.content,
                aiResponse: msg.aiResponse,
                image: msg.aiResponse?.image,
                fullAiResponse: JSON.stringify(msg.aiResponse, null, 2)
              });
              console.log(`🔍 Message ${idx} aiResponse expanded:`, msg.aiResponse);
            });
          }
          setConversation(responseData.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('ConversationPanel: Error fetching conversation:', err);
        setError('Failed to fetch conversation');
      } finally {
        setLoading(false);
      }
    };

    fetchConversation();
    const interval = setInterval(fetchConversation, 3000); // Poll every 3 seconds
    return () => clearInterval(interval);
  }, [activeConversationId]);

  console.log('ConversationPanel: Rendering with isClosed:', isClosed);
  
  if (isClosed) {
    return null;
  }

  if (loading && !conversation) {
    return (
      <Box sx={{ p: 2 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!conversation) {
    return (
      <Box sx={{ p: 2, bgcolor: 'yellow', border: '2px solid red' }}>
        <Typography variant="h6">No conversation selected.</Typography>
        <Typography variant="body2">ActiveConversationId: {activeConversationId || 'None'}</Typography>
      </Box>
    );
  }

  const formatDate = (date: Date | string) => {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'archived': return 'warning';
      case 'deleted': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Paper 
        elevation={2} 
        sx={{ 
          p: 0, 
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        {/* Header Section */}
        <Box sx={{ 
          p: 3, 
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 600,
                  mb: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {conversation.title}
              </Typography>
              
              {/* Metadata Summary */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Chip 
                  label={conversation.status} 
                  size="small" 
                  color={getStatusColor(conversation.status) as any}
                  variant="outlined"
                />
                <Chip 
                  label={conversation.type} 
                  size="small" 
                  variant="outlined"
                  sx={{ textTransform: 'capitalize' }}
                />
                {conversation.metadata?.messageCount && (
                  <Chip 
                    label={`${conversation.metadata.messageCount} messages`}
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton 
                onClick={() => setIsClosed(true)}
                size="small"
                sx={{ ml: 1 }}
              >
                <CloseIcon />
              </IconButton>
              <ShareButton onClick={() => {
                setShareMessages([]);
                setShareImages([]);
                setShareOpen(true);
              }} ariaLabel="Share conversation" inline={true} />
            </Box>
          </Stack>

          {/* Collapsible Metadata Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              onClick={() => setMetadataExpanded(!metadataExpanded)}
              sx={{ 
                p: 0.5,
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' }
              }}
            >
              {metadataExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ cursor: 'pointer' }}
              onClick={() => setMetadataExpanded(!metadataExpanded)}
            >
              Details
            </Typography>
          </Box>
        </Box>

        {/* Collapsible Metadata Content */}
        <Collapse in={metadataExpanded}>
          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.50',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}>
            <Stack spacing={1}>
              {conversation.metadata && (
                <>
                  {conversation.metadata.aiModel && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                        AI Model:
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {conversation.metadata.aiModel}
                      </Typography>
                    </Box>
                  )}
                  
                  {conversation.metadata.sessionDuration && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                        Duration:
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {Math.round(conversation.metadata.sessionDuration / 60)} minutes
                      </Typography>
                    </Box>
                  )}
                  
                  {conversation.metadata.lastActivity && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                        Last Activity:
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {formatDate(conversation.metadata.lastActivity)}
                      </Typography>
                    </Box>
                  )}
                  
                  {conversation.metadata.lastMessageAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                        Last Message:
                      </Typography>
                      <Typography variant="caption" color="text.primary">
                        {formatDate(conversation.metadata.lastMessageAt)}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </Box>
        </Collapse>

        {/* Messages Section */}
        <Box sx={{ p: 3, pt: 2 }}>
          {/* Search and Filter Controls */}
          {conversation.messages && conversation.messages.length > 0 && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <FilterListIcon fontSize="small" />
                Search & Filter Messages
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'center' }}>
                {/* Search Input */}
                <TextField
                  size="small"
                  placeholder="Search message content..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearchTerm('')}
                          edge="end"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ flex: 1, minWidth: 200 }}
                />

                {/* Message Type Filter */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={filterType}
                    onChange={(e) => setFilterType(e.target.value)}
                    label="Type"
                  >
                    <MenuItem value="all">All Types</MenuItem>
                    <MenuItem value="user">User Messages</MenuItem>
                    <MenuItem value="ai">AI Responses</MenuItem>
                  </Select>
                </FormControl>

                {/* Date Filter */}
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Date</InputLabel>
                  <Select
                    value={filterDate}
                    onChange={(e) => setFilterDate(e.target.value)}
                    label="Date"
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="today">Today</MenuItem>
                    <MenuItem value="week">This Week</MenuItem>
                    <MenuItem value="month">This Month</MenuItem>
                  </Select>
                </FormControl>

                {/* Clear Filters Button */}
                {(searchTerm || filterType !== 'all' || filterDate !== 'all') && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setSearchTerm('');
                      setFilterType('all');
                      setFilterDate('all');
                    }}
                    startIcon={<ClearIcon />}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>

              {/* Filter Summary */}
              {(searchTerm || filterType !== 'all' || filterDate !== 'all') && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Filtered by:
                  </Typography>
                  {searchTerm && (
                    <Chip label={`"${searchTerm}"`} size="small" variant="outlined" />
                  )}
                  {filterType !== 'all' && (
                    <Chip label={filterType} size="small" variant="outlined" />
                  )}
                  {filterDate !== 'all' && (
                    <Chip label={filterDate} size="small" variant="outlined" />
                  )}
                  <Chip 
                    label={`${filteredMessages.length} of ${conversation.messages?.length || 0} messages`} 
                    size="small" 
                    variant="outlined" 
                    color="primary"
                  />
                </Box>
              )}
            </Box>
          )}

          {conversation.messages && conversation.messages.length > 0 ? (
            <Stack spacing={2}>
              {filteredMessages?.map((message, idx) => (
                <MessageBubble
                  key={message._id}
                  content={message.content}
                  type={message.type}
                  createdAt={message.createdAt}
                  isLatest={idx === (conversation.messages?.length ?? 0) - 1}
                  image={message.aiResponse?.image}
                  onShare={() => {
                    setShareMessages([message]);
                    setShareImages(message.aiResponse?.image ? [message.aiResponse.image] : []);
                    setShareOpen(true);
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Box sx={{ 
              p: 3, 
              textAlign: 'center', 
              bgcolor: 'grey.50', 
              borderRadius: 2,
              border: `1px dashed ${theme.palette.divider}`
            }}>
              <InfoIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No messages in this conversation yet.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Start a conversation by typing a message below.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        onTextChange={setShareText}
        onShare={(platform, images) => setShareOpen(false)}
        conversationMessages={shareMessages.length > 0 ? shareMessages : conversation?.messages || []}
        conversationImages={shareImages.length > 0 ? shareImages : getConversationImages(conversation?.messages)}
      />
    </Box>
  );
};

export default ConversationPanel;